<template>
  <div class="m-tabs">
    <div class="m-tabs__container">
      <div
        class="m-tabs__indicator m-tabs__indicator--left"
        v-if="scrollLeft"
        :class="{ 'side-active': iconLeft }"
        @click="scrollTo('left')">
        <ChevronLeftIcon class="m-tabs__indicator--left__chevron" />
        <FontIcon :name="iconLeft" class="m-tabs__indicator--left__active-icon" v-if="iconLeft" />
      </div>
      <div
        class="m-tabs__indicator m-tabs__indicator--right"
        v-if="scrollRight"
        :class="{ 'side-active': iconRight }"
        @click="scrollTo('right')">
        <ChevronRightIcon class="m-tabs__indicator--right__chevron" />
        <FontIcon :name="iconRight" class="m-tabs__indicator--right__active-icon" v-if="iconRight" />
      </div>
      <div class="m-tabs__tabs" @scroll="determineSideIcons()" ref="tabs">
        <RouterLink
          v-for="(tab, index) in tabs"
          :key="index"
          :class="`m-tabs__tabs__tab${tab.active ? ' m-tabs__tabs__tab--active' : ''}`"
          :to="tab.route"
          :data-id="index"
          :ref="`tab-${index}`"
          @click.native="setActiveTab(tab)">
          <FontIcon :name="tab.icon" v-if="tab.icon" />
          <span>{{ tab.title }}</span>
        </RouterLink>
      </div>
    </div>
    <div class="m-tabs__content">
      <RouterView />
    </div>
  </div>
</template>

<script>
import FontIcon from '@/components/Icon.vue';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons/fontIcons';

export default {
  name: 'VTabsRouter',
  introduction: 'Displays an array of router views, one at a time, through a series of scrollable tabs.',
  components: {
    FontIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
  },
  props: {
    tabs: {
      type: Array,
      required: true,
      note: 'An array of tab objects with { title, icon, route }.',
    },
    icons: {
      type: Array,
      required: false,
      note: 'Unused needs removal.',
    },
  },
  data() {
    return {
      activeTab: null,
      iconLeft: null,
      iconRight: null,
      scrollLeft: false,
      scrollRight: false,
    };
  },
  created() {
    window.addEventListener('resize', this.determineSideIcons);
  },
  mounted() {
    const [activeTab] = this.tabs.filter(tab => tab.route === this.$route.path);
    this.activeTab = activeTab;
    this.$nextTick(() => {
      this.determineSideIcons();
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.determineSideIcons);
  },
  methods: {
    determineSideIcons() {
      this.determineScrollDirections();

      const tabDiv = this.$refs.tabs;
      const el = document.querySelector('.m-tabs__tabs__tab.router-link-active');

      if (tabDiv.scrollLeft >= (el.offsetLeft + el.offsetWidth)) {
        this.iconLeft = this.activeTab.icon;
      } else if ((el.offsetLeft) >= (tabDiv.scrollLeft + tabDiv.offsetWidth)) {
        this.iconRight = this.activeTab.icon;
      } else {
        this.iconLeft = null;
        this.iconRight = null;
      }
    },
    setActiveTab(tab) {
      this.iconLeft = null;
      this.iconRight = null;
      this.activeTab = tab;
      this.$nextTick(() => {
        this.determineSideIcons();
      });
    },
    determineScrollDirections() {
      const tabDiv = this.$refs.tabs;
      const lastTab = this.$refs[`tab-${this.tabs.length - 1}`][0].$el;

      if (tabDiv.scrollLeft > 10) {
        this.scrollLeft = true;
      } else {
        this.scrollLeft = false;
      }

      if ((tabDiv.scrollLeft + tabDiv.offsetWidth + 10)
          <= (lastTab.offsetLeft + lastTab.offsetWidth)) {
        this.scrollRight = true;
      } else {
        this.scrollRight = false;
      }
    },
    scrollTo(direction) {
      if (direction === 'right') {
        this.$refs.tabs.scrollLeft += 100;
      } else if (direction === 'left') {
        this.$refs.tabs.scrollLeft -= 100;
      }
    },
  },
};
</script>

<style scoped>
.m-tabs__container {
  overflow: hidden;
  height: 50px;
  margin-bottom: -3px;
  background: #fff;
  border-top: 1px solid var(--color-border);
  border-right: 1px solid var(--color-border);
  border-left: 1px solid var(--color-border);
}

.m-tabs__tabs {
  white-space: nowrap;
  overflow-x: scroll;
  overflow-y: hidden;
  padding-bottom: 60px;
  position: relative;
}

.m-tabs__tabs__tab {
  display: inline-block;
  padding: var(--spacing-base);
  cursor: pointer;
  margin-bottom: -3px;
  min-width: 80px;
  line-height: 1;
  color: #555;
  text-decoration: none;
}

.m-tabs__tabs__tab:hover {
  border-bottom: 3px solid var(--signable-secondary-2-shade-2);
}

.m-tabs__tabs__tab svg {
  float: left;
  margin-right: var(--spacing-small);
  height: 16px;
  width: 16px;
}

.m-tabs__tabs__tab svg,
.m-tabs__tabs__tab span {
  pointer-events: none;
}

.m-tabs__tabs__tab.router-link-active {
  border-bottom: 3px solid var(--signable-primary-1);
}

.m-tabs__tabs__tab.router-link-active:hover {
  border-bottom: 3px solid var(--signable-primary-1);
}

.m-tabs__content {
  padding: var(--spacing-base);
  border: 1px solid var(--color-border);
  border-top: 3px solid var(--color-border);
  background: #fff;
}

.m-tabs__indicator {
  padding: var(--spacing-small);
  margin-top: var(--spacing-xs);
  height: calc(100% - 13px);
  max-width: 50px;
  transition: all 0.4s ease;
  position: relative;
}

.m-tabs__indicator--left {
  padding-left: var(--spacing-base);
  float: left;
  border-right: 1px solid var(--signable-secondary-4-shade-1);
}

.m-tabs__indicator--left__chevron {
  position: absolute;
  left: var(--spacing-xs);
  color: var(--signable-secondary-4-shade-1);
}

.m-tabs__indicator--left__active-icon {
  margin-left: var(--spacing-small);
}

.m-tabs__indicator--right {
  padding-right: var(--spacing-base);
  float: right;
  border-left: 1px solid var(--signable-secondary-4-shade-1);
}

.m-tabs__indicator--right__chevron {
  position: absolute;
  right: var(--spacing-xs);
  color: var(--signable-secondary-4-shade-1);
}

.m-tabs__indicator--right__active-icon {
  margin-right: var(--spacing-small);
}

.side-active:after {
  content: '';
  width: 100%;
  height: 3px;
  background: var(--signable-primary-1);
  position: absolute;
  bottom: -8px;
  left: 0;
}
</style>
